/**
 * Lazy, pausable autoplay video for the ported PDP sections.
 *
 * Why this exists: <video autoplay> makes the browser ignore preload="metadata"
 * and buffer immediately, so nine off-screen videos pulled ~2MB before the
 * visitor scrolled at all. It also left no way to stop the motion (WCAG 2.2.2).
 *
 * Contract:
 *   <video data-onz-src="…" muted loop playsinline preload="none">
 *   Container: [data-onz-video-scope]
 *   Optional toggle: [data-onz-video-toggle] inside that scope
 */
(function () {
  if (window.__onzVideoInit) return;
  window.__onzVideoInit = true;

  var LOAD_MARGIN = '400px'; // start fetching just before the tile is reached
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function load(video) {
    if (video.dataset.onzSrc && !video.src) {
      video.src = video.dataset.onzSrc;
    }
  }

  function play(video) {
    load(video);
    var scope = video.closest('[data-onz-video-scope]');
    if (scope && scope.dataset.onzPaused === 'true') return;
    if (reduced.matches) return;
    var p = video.play();
    if (p && p.catch) p.catch(function () { /* autoplay refused — poster stays */ });
  }

  var io = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) play(e.target);
          else if (!e.target.paused) e.target.pause();
        });
      }, { rootMargin: LOAD_MARGIN, threshold: 0.1 })
    : null;

  function wire(root) {
    (root || document).querySelectorAll('video[data-onz-src]').forEach(function (v) {
      if (v.dataset.onzWired) return;
      v.dataset.onzWired = '1';
      if (io) io.observe(v);
      else { load(v); play(v); }   // no observer: behave as before rather than show nothing
    });

    (root || document).querySelectorAll('[data-onz-video-toggle]').forEach(function (btn) {
      if (btn.dataset.onzWired) return;
      btn.dataset.onzWired = '1';
      btn.addEventListener('click', function () {
        var scope = btn.closest('[data-onz-video-scope]');
        if (!scope) return;
        var paused = scope.dataset.onzPaused === 'true';
        scope.dataset.onzPaused = paused ? 'false' : 'true';
        btn.setAttribute('aria-pressed', paused ? 'false' : 'true');
        scope.querySelectorAll('video').forEach(function (v) {
          if (paused) play(v);
          else v.pause();
        });
      });
      // Reduced motion: nothing is playing, so the control offers to start.
      if (reduced.matches) {
        var scope = btn.closest('[data-onz-video-scope]');
        if (scope) scope.dataset.onzPaused = 'true';
        btn.setAttribute('aria-pressed', 'true');
      }
    });
  }

  // Sound toggle for the reel tiles. Lives here (not in an inline <script>)
  // because document.currentScript is null on a theme-editor re-render, which
  // silently killed the old handler.
  function wireSound(root) {
    (root || document).querySelectorAll('.onz-reels[data-onz-video-scope]').forEach(function (scope) {
      if (scope.dataset.onzSoundWired) return;
      scope.dataset.onzSoundWired = '1';
      scope.addEventListener('click', function (e) {
        var tile = e.target.closest('.onz-reels__tile--video');
        var v = tile && tile.querySelector('video');
        if (!v) return;
        var turningOn = v.muted;
        scope.querySelectorAll('.onz-reels__tile--video').forEach(function (t) {
          var o = t.querySelector('video');
          if (o) o.muted = true;
          t.classList.remove('is-unmuted');
          t.setAttribute('aria-pressed', 'false');
        });
        if (!turningOn) return;
        v.muted = false;
        tile.classList.add('is-unmuted');
        tile.setAttribute('aria-pressed', 'true');
        var p = v.play();
        if (p && p.catch) {
          p.catch(function () {
            // Playback refused — do not claim sound is on.
            v.muted = true;
            tile.classList.remove('is-unmuted');
            tile.setAttribute('aria-pressed', 'false');
          });
        }
      });
    });
  }

  var wireAll = function (root) { wire(root); wireSound(root); };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { wireAll(); });
  } else {
    wireAll();
  }
  // Theme editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', function (e) { wireAll(e.target); });
})();
